import * as date_fns from 'date-fns'
import { addSeconds, Locale, subSeconds } from 'date-fns'
import { getTimezoneOffset } from 'date-fns-tz'
import { DateFormat } from '../enums'

export const formatDateV2 = (
  date: Date | string | null | undefined,
  dateFormat: DateFormat,
  locale?: Locale,
): string | null => {
  if (!date) {
    return null
  }
  // date-fns v2 no longer accepts strings directly, convert to Date first
  const parsed: Date = typeof date === 'string' ? date_fns.parseISO(date) : date
  if (!date_fns.isValid(parsed)) {
    return null
  }
  return date_fns.format(parsed, dateFormat, locale ? { locale } : undefined)
}

export const setDateToTimezone = (date: Date, timezone: string): Date =>
  addSeconds(date, getTimezoneOffset(timezone, date) / 1000)

// This method must be used before storing a date in the database so it's converted to UTC before being stored
export const setDateStringToTimezone = (dateString: string, timezone: string): string =>
  addSeconds(new Date(dateString), getTimezoneOffset(timezone, new Date(dateString)) / 1000).toISOString()

// This method must be used when receiving data from the database so it's converted to the local timezone
export const getDateStringForTimezone = (dateString: string, timezone: string): string =>
  subSeconds(new Date(dateString), getTimezoneOffset(timezone, new Date(dateString)) / 1000).toISOString()

// This method must be used when receiving data from the database so it's converted to the local timezone
export const getDateForTimezone = (date: Date, timezone: string): Date =>
  subSeconds(date, getTimezoneOffset(timezone, date) / 1000)
/**
 * Formats a given date based on the specified user format.
 *
 * @param date - The date that needs to be formatted, the date should be DateFormat
 * @param userFormat - The desired output format, see DateFormat enum
 *
 *
 * @returns {string} - The date formatted according to the specified format.
 *
 */
export const formatDateForUser = (date: Date, userFormat: DateFormat): string => date_fns.format(date, userFormat)

/**
 * Coerces a value that may be a `Date`, an ISO string (e.g. a Date-typed DTO field after JSON
 * deserialization over HTTP), `null`, or `undefined` into a real `Date` instance or `null`.
 * Useful before handing a value to a form control that only accepts `null`/`Date` (rejecting
 * plain strings silently rather than throwing).
 */
export const toDateOrNull = (value: Date | string | null | undefined): Date | null => {
  if (!value) {
    return null
  }
  const parsed: Date = typeof value === 'string' ? date_fns.parseISO(value) : value
  return date_fns.isValid(parsed) ? parsed : null
}
